'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

export type ProductCardProps = {
  product: Product;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, className }: ProductCardProps) {
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const firstSupplier = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position)[0];
  const actionLabel = t(firstSupplier?.is_affiliate ? 'text1226' : 'text1115').trim() ||
    (firstSupplier?.is_affiliate ? 'Prejsť na ponuku' : 'Pridať do košíka');

  return (
    <div className={cn('', 'ea-cproduct-card')}>
      <Link prefetchOnHover
        prefetch={false}
        href={productUrl}
        className={cn(
          'relative h-auto w-full max-w-[200px] rounded-md',
          'ea-cproduct-card__image group overflow-hidden',
        )}
      >
        <Image
          src={product?.image?.url || '/images/placeholder.png'}
          alt={resolveImageAlt(product?.image, product?.name, product?.name || 'Product Image')}
          height={0}
          width={0}
          sizes="100vw"
          className="h-auto w-auto scale-110 transform transition-transform duration-300 ease-in-out group-hover:scale-[1.2]" // Added transition and scaling on hover
        />
      </Link>
      <div className={cn('', 'ea-cproduct-card__content')}>
        <h3 data-ea-visual-tag="h5">
          <Link prefetchOnHover prefetch={false} href={productUrl} className="text-primary hover:underline">
            {product.name}
          </Link>
        </h3>
        <HtmlRendererRaw
          className={cn('', 'ea-cproduct-card__short-description')}
          html={product.short_description}
        />
        <div className={cn('', 'ea-cproduct-card__footer')}>
          {priceDisplay.priceOnRequest ? (
            <span>{t('text2278')}</span>
          ) : (
            <div className="flex flex-col">
              {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
                <span className="text-sm text-muted-foreground">
                  {`${t('text1926')} ${formatPrice(
                    priceDisplay.mocPrice,
                    generalLayout.currency,
                    generalLayout.currency_settings,
                  )}`}
                </span>
              ) : null}
              <div className="flex items-baseline gap-2">
                <strong>
                  {formatPrice(
                    priceDisplay.current,
                    generalLayout.currency,
                    generalLayout.currency_settings,
                  )}
                </strong>
                {priceDisplay.showPreviousPrice ? (
                  <del className="text-sm text-muted-foreground">
                    {formatPrice(
                      priceDisplay.beforeDiscount,
                      generalLayout.currency,
                      generalLayout.currency_settings,
                    )}
                  </del>
                ) : null}
              </div>
              {templateSettings.product_teaser?.price_without_vat?.enabled ? (
                <span className="text-sm text-muted-foreground">
                  {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                  {formatPrice(
                    priceDisplay.secondary,
                    generalLayout.currency,
                    generalLayout.currency_settings,
                  )}{' '}
                  {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                </span>
              ) : null}
            </div>
          )}
          <div className={cn('', 'ea-cproduct-card__buttons')}>
            {!priceDisplay.priceOnRequest ? (
              <AddToCartButton
                product={product}
                aria-label={`${actionLabel}: ${product.name}`}
                showIcon={!!templateSettings.product_teaser?.cart_icon}
                sellable={true}
                className={cn('', 'ea-product-card__add-to-cart-button')}
              />
            ) : null}
            <Button asChild variant={'outline'} size={'icon-lg'}>
              <Link prefetchOnHover prefetch={false} href={productUrl}>
                <Image src="/images/detail.svg" height={32} width={32} alt={product.slug} />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider state={{ product: props.product }}>
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
